import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import get from '~/util/getOr';
import FootballTeamPossession from '~/components/FootballTeamPossession/FootballTeamPossession';
import Link from '~/components/Link/Link';
import Styled from '~/components/Styled/Styled';
import { ResponsivePNG } from 'sports-react';
import moment from 'moment';
import css from './EventCard.scss';
import BaseballBases from '../BaseballBases/BaseballBases';

const cssContext = classnames.bind(css);

const isHomeFirst = event => event.league.sport_name === 'soccer';

/* eslint-disable camelcase */

const DisplayFootballPossessionStats = ({ sportName, status, boxScore }) => {
  const down = get(boxScore, 'down', '');
  const formattedDistance = get(boxScore, 'formatted_distance', '');
  const fieldPosition = get(boxScore, 'field_position', '');

  if (
    sportName === 'football' &&
    status === 'in_progress' &&
    down &&
    formattedDistance &&
    fieldPosition
  ) {
    return (
      <div className={css.footballStatus}>
        <span>
          {moment.localeData().ordinal(down)} and {formattedDistance} on {fieldPosition}
        </span>
      </div>
    );
  }

  return null;
};

class EventCard extends React.Component {
  /* prettier-ignore */
  teamDidLose(team) {
    const {
      event: { box_score, status },
    } = this.props;

    if (!team) return false;
    if (!box_score) return false; // eslint-disable-line camelcase
    if (status !== 'final') return false;
    if (box_score.score.tie_game) return false;
    return team.api_uri === box_score.score.losing_team;
  }

  /* prettier-ignore */
  allowGameDescription() {
    const {
      event: {
        game_description,
        league: { slug },
      },
    } = this.props;

    const slugs = ['ncaab', 'ncaaf', 'wcbk', 'nhl', 'chlg', 'uefa', 'mlb'];

    return slugs.includes(slug) && game_description;
  }

  /* prettier-ignore */
  renderClock() {
    const {
      event: { status, box_score, game_date, tba },
    } = this.props;
    const gameDate = moment(game_date).format('h:mm A');
    const clockLabel = get(box_score, 'progress.clock_label', gameDate);

    if (tba) {
      return 'TBD';
    }

    switch (status) {
      case 'pre_game':
        return gameDate;
      case 'postponed':
        return 'PPD';
      case 'half_over':
        return 'Half';
      case 'in_progress':
      case 'final':
      default:
        return clockLabel;
    }
  }

  /* prettier-ignore */
  renderGameDescription() {
    const {
      event: { game_description },
    } = this.props;

    const className = cssContext(css.gameDescription);

    if (game_description) {
      return (
        <span className={className}>
          {game_description.split('\n').map(description => (
            <Fragment>
              <span>{description}</span>
              <br />
            </Fragment>
          ))}
        </span>
      );
    }

    return null;
  }

  renderTeam(
    displayFootballPossession = false,
    redZone,
    eventStatus,
    team,
    odd,
    score,
    ranking,
    boxScore,
    sportName,
    displayBaseballInningStatus = false,
    isTop = false,
    displaySoccerAggregate = null
  ) {
    const lost = this.teamDidLose(team);

    const eventRow = cssContext(css.teamRow, {
      topRow: isTop,
    });

    const teamName = cssContext(css.teamName, {
      teamLost: lost,
    });

    const scoreText = cssContext(css.score, {
      teamLost: lost,
    });

    const logo = team ? team.logos.w72xh72 : '';
    const name = team ? team.medium_name || team.short_name : 'TBD';

    return (
      <div className={eventRow}>
        <div className={css.teamColumn}>
          <div className={css.teamLogo}>
            {logo ? (
              <ResponsivePNG
                className={css.logo}
                src={logo}
                alt={`${name} logo`}
                height="24px"
                width="24px"
                styles="{transform: 'translateY(6px)'}"
              />
            ) : (
              <div className={css.defaultLogo} />
            )}
          </div>
          <div className={teamName}>
            {ranking && <div className={css.ranking}>{`(${ranking}) `}</div>}
            {name && <div>{name}</div>}
          </div>
        </div>
        <div className={css.scoreColumn}>
          {eventStatus === 'pre_game' ? <div className={css.pregameScoreText}>{odd}</div> : null}
          {eventStatus !== 'pre_game' && eventStatus !== 'postponed' ? (
            <div className={css.scoreContainer}>
              {displayFootballPossession ? (
                <FootballTeamPossession
                  fillColor={redZone ? '#ff0f00' : '#0078fd'}
                  style={{
                    height: '14px',
                    width: '14px',
                  }}
                />
              ) : null}
              <div className={scoreText}>{score}</div>
            </div>
          ) : null}
        </div>
        <div className={css.clockColumn}>
          {isTop ? this.renderClock() : null}

          {!isTop && displayBaseballInningStatus ? (
            <div className={css.baseballBaseStatus}>
              <BaseballBases
                firstBase={boxScore.first_base}
                secondBase={boxScore.second_base}
                thirdBase={boxScore.third_base}
              />
              <span>{`${boxScore.outs} OUT`}</span>
            </div>
          ) : null}

          {!isTop && displaySoccerAggregate ? (
            <div className={css.aggregate}>{displaySoccerAggregate.short_description}</div>
          ) : null}
        </div>
      </div>
    );
  }

  /* prettier-ignore */
  render() {
    const {
      event: { status, odd, home_team, away_team, box_score, api_uri, league },
    } = this.props;

    const homeFirst = isHomeFirst(this.props.event);
    const homeScore = box_score ? box_score.score.home.score : null; // eslint-disable-line camelcase
    const awayScore = box_score ? box_score.score.away.score : null; // eslint-disable-line camelcase
    const homeRanking = get(this.props.event, 'top_25_rankings.home', null);
    const awayRanking = get(this.props.event, 'top_25_rankings.away', null);
    const homeOdd = odd ? odd.home_odd : null;
    const awayOdd = odd ? odd.away_odd : null;
    const eventCardContainerCSS = cssContext(css.eventCardContainer, {
      gameDescriptionAddition: this.allowGameDescription(),
    });
    const awayTeamAbbreviation = get(away_team, "abbreviation", "")
    const homeTeamAbbreviation = get(home_team, "abbreviation", "")
    const teamInPossession = get(this.props.event, 'box_score.team_in_possession.abbreviation', '');
    const displayBaseballInningStatus =
      league.sport_name === 'baseball' &&
      status === 'in_progress' &&
      Object.prototype.hasOwnProperty.call(box_score, 'balls') &&
      Object.prototype.hasOwnProperty.call(box_score, 'strikes') &&
      Object.prototype.hasOwnProperty.call(box_score, 'outs') &&
      (box_score.progress.clock === 'Top' || box_score.progress.clock === 'Bot');

    const displaySoccerAggregate =
      (league.sport_name === 'soccer' && this.props.event.aggregate_result) || false;

    const redZone = get(this.props.event, "red_zone", false)

    if (homeFirst) {
      return (
        <Link route={api_uri} prefetch>
          <a>
            <div className={eventCardContainerCSS}>
              {this.renderTeam(
                homeTeamAbbreviation === teamInPossession,
                redZone,
                status,
                home_team,
                homeOdd,
                homeScore,
                homeRanking,
                box_score,
                league.sport_name,
                displayBaseballInningStatus,
                true,
                false,

              )}
              {this.renderTeam(
                awayTeamAbbreviation === teamInPossession,
                redZone,
                status,
                away_team,
                awayOdd,
                awayScore,
                awayRanking,
                box_score,
                league.sport_name,
                displayBaseballInningStatus,
                false,
                displaySoccerAggregate,

              )}
              {this.allowGameDescription() ? this.renderGameDescription() : null}
            </div>
          </a>
        </Link>
      );
    }

    return (
      <Link route={api_uri} prefetch>
        <a>
          <div className={eventCardContainerCSS}>
            {this.renderTeam(
              awayTeamAbbreviation === teamInPossession,
              redZone,
              status,
              away_team,
              awayOdd,
              awayScore,
              awayRanking,
              box_score,
              league.sport_name,
              displayBaseballInningStatus,
              true,
            )}
            {this.renderTeam(
              homeTeamAbbreviation === teamInPossession,
              redZone,
              status,
              home_team,
              homeOdd,
              homeScore,
              homeRanking,
              box_score,
              league.sport_name,
              displayBaseballInningStatus,
              false,
            )}
            {this.allowGameDescription() ? this.renderGameDescription() : null}
            <DisplayFootballPossessionStats sportName={league.sport_name} status={status} boxScore={box_score} />
          </div>
        </a>
      </Link>
    );
  }
}

DisplayFootballPossessionStats.displayName = 'DisplayFootballPossessionStats';
DisplayFootballPossessionStats.defaultProps = {
  sportName: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  boxScore: PropTypes.object,
};

DisplayFootballPossessionStats.propTypes = {
  sportName: '',
  status: '',
  boxScore: {},
};

EventCard.propTypes = {
  event: PropTypes.object.isRequired,
};

EventCard.displayName = 'EventCard';

export default Styled(EventCard, [css]);
